if (!customElements.get('bundle-promo-modal')) {
  class BundlePromoModal extends HTMLElement {
    constructor() {
      super();

      this.onCloseClick = this.onCloseClick.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      // Relocate to <body> first, before any setup. Appending triggers
      // disconnectedCallback + a second connectedCallback, so doing this
      // before `initialized` is set means setup runs exactly once, on the
      // final insertion point, and the open timer can't be cleared by the move.
      if (this.parentElement !== document.body) {
        this.isMoving = true;
        document.body.appendChild(this);
        this.isMoving = false;
        return;
      }

      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('[role="dialog"]');
      this.form = this.querySelector('[data-bundle-promo-form]');
      this.submitButton = this.querySelector('[data-bundle-promo-submit]');
      this.errorEl = this.querySelector('[data-bundle-promo-error]');

      this.delay = parseInt(this.dataset.delay, 10) || 0;
      this.frequency = this.dataset.frequency || 'session';
      this.storageKey = `bundle-promo-dismissed:${this.dataset.promoId || 'default'}`;

      this.querySelectorAll('[data-bundle-promo-close]').forEach((el) =>
        el.addEventListener('click', this.onCloseClick)
      );
      this.addEventListener('click', this.onModalClick);
      this.addEventListener('keyup', this.onKeyUp);
      this.form?.addEventListener('submit', this.onSubmit);

      if (this.hasBeenDismissed()) return;

      this.timer = setTimeout(() => this.show(), this.delay);
    }

    disconnectedCallback() {
      if (this.isMoving) return;

      clearTimeout(this.timer);
      this.removeEventListener('click', this.onModalClick);
      this.removeEventListener('keyup', this.onKeyUp);
      this.form?.removeEventListener('submit', this.onSubmit);

      if (this.hasAttribute('open')) this.hide(false);
    }

    get storage() {
      try {
        return this.frequency === 'always'
          ? null
          : this.frequency === 'session'
            ? window.sessionStorage
            : window.localStorage;
      } catch (e) {
        return null;
      }
    }

    hasBeenDismissed() {
      if (this.frequency === 'always') return false;
      try {
        return this.storage?.getItem(this.storageKey) === '1';
      } catch (e) {
        return false;
      }
    }

    markDismissed() {
      try {
        this.storage?.setItem(this.storageKey, '1');
      } catch (e) {
        /* storage unavailable — fail open */
      }
    }

    onCloseClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }

    onModalClick(event) {
      if (event.target === this) this.hide();
    }

    onKeyUp(event) {
      if (event.code?.toUpperCase() !== 'ESCAPE') return;
      event.preventDefault();
      this.hide();
    }

    lockScroll() {
      // Track prior state so we don't unlock a drawer that was already open.
      this.bodyOverflowWasHidden = document.body.classList.contains('overflow-hidden');
      if (!this.bodyOverflowWasHidden) document.body.classList.add('overflow-hidden');
    }

    unlockScroll() {
      if (!this.bodyOverflowWasHidden) document.body.classList.remove('overflow-hidden');
    }

    show() {
      if (this.hasAttribute('open')) return;

      this.previouslyFocused = document.activeElement;
      this.lockScroll();
      this.setAttribute('open', '');

      if (typeof trapFocus === 'function') {
        trapFocus(this, this.dialog);
      } else {
        this.dialog?.focus();
      }
    }

    hide(markDismissed = true) {
      if (!this.hasAttribute('open')) return;

      this.removeAttribute('open');
      this.unlockScroll();

      if (markDismissed) this.markDismissed();

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.previouslyFocused);
      } else {
        this.previouslyFocused?.focus?.();
      }
    }

    onSubmit(event) {
      event.preventDefault();

      if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      this.clearError();

      // This theme loads cart-drawer.js but not cart-notification.js, so a
      // <cart-notification> in the DOM stays an unupgraded element: truthy but
      // without renderContents. Select by capability rather than presence.
      const cart = [
        document.querySelector('cart-drawer'),
        document.querySelector('cart-notification'),
      ].find((el) => el && typeof el.getSectionsToRender === 'function' && typeof el.renderContents === 'function');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      const variantId = formData.get('id');

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message);
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'bundle-promo-modal',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            return;
          }

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-promo-modal',
              productVariantId: variantId,
              cartData: response,
            });
          }

          this.markDismissed();

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.hide(true);
          // hide() releases the body scroll lock; the drawer re-applies its own
          // in open(). Render after the modal has fully closed so the drawer's
          // lock is the one that sticks.
          requestAnimationFrame(() => cart.renderContents(response));
        })
        .catch((e) => {
          console.error(e);
          this.showError(window.cartStrings?.error || 'Something went wrong. Please try again.');
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;

      this.submitButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
    }

    clearError() {
      this.errorEl?.classList.add('hidden');
    }
  }

  customElements.define('bundle-promo-modal', BundlePromoModal);
}
